import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { MoreVertical, AlertTriangle, UserX } from "lucide-react";
import { useDisclosure } from "@mantine/hooks";
import RemoveCustomerFromAppointmentModal from "./RemoveCustomerFromAppointmentModal";
import { Fragment, useMemo, useState } from "react";
import {
  Button,
  Chip,
  AccordionItem,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Divider,
} from "@heroui/react";
import dayjs from "dayjs";
import { BsCurrencyEuro } from "react-icons/bs";
import { LimeAccordion } from "../../NextBase/LimeAccordion";
import { getUserAppointmentCancelReasonOptions } from "@/constants/cancelReasons";
import { cn } from "@/utils";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import type { SubscriptionCreditType } from "@/server-types";
import { AddFixedMemberModal } from "@/pages/Dashboard/FixedAppointments/AddFixedMemberModal";
import { RemoveFixedMemberModal } from "@/pages/Dashboard/FixedAppointments/RemoveFixedMemberModal";
import { WaitlistEntries } from "./WaitlistEntries";
import { getOutstandingSubscriptionPeriods } from "./CustomerSubscriptions/subscriptionPaymentStatus";

type Customer = {
  show: boolean;
  name: string;
  lastName: string;
  userAppointmentId: number;
  customerId: number;
  isDeleted?: boolean;
  isFirstAppointment: boolean;
  cancelReason?: string;
  customCancelReason?: string;
  paymentStatus?: string;
  paymentIntentId?: string | null;
  customerDidAttend: number;
  customerSubscriptionId: number;
  subscriptionName?: string | null;
  subscriptionStatus?: string | null;
  isUsedSubscriptionCurrentlyFrozen: boolean;
  customerSubscriptionCredit?: {
    id: number;
    type: SubscriptionCreditType;
  } | null;
  subscription?: {
    id: number;
    status: string;
    validFrom: Date;
    validTo: Date;
    subscriptionName?: string | null;
    variationName?: string | null;
    subscription?: {
      nameLocalized: Array<{
        language: string;
        value: string;
      }>;
    };
    variation?: {
      nameLocalized: Array<{
        language: string;
        value: string;
      }>;
    };
    invoices: Array<{
      periodStart: Date;
      periodEnd: Date;
      /** The customer still owes their share. A company's arrears are not theirs. */
      memberOwes: boolean;
      periodStartFormatted: string;
      periodEndFormatted: string;
    }>;
    ScheduledSubscriptionFreeze?: Array<{
      id: number;
      freezeStart: Date;
      freezeEnd: Date;
      freezeReason: string;
    }>;
  };
};

const getDisplayedCancelReason = (
  customer: Pick<
    Customer,
    "paymentStatus" | "cancelReason" | "customCancelReason"
  >,
): string => {
  if (customer.paymentStatus === "canceled") {
    return t`Stranka ni plačala`;
  }

  if (customer.cancelReason === "Custom" && customer.customCancelReason) {
    return customer.customCancelReason;
  }

  const mapped = getUserAppointmentCancelReasonOptions().find(
    (r) => r.value === customer.cancelReason,
  )?.label;

  return mapped ?? t`Ni razloga`;
};

export const CustomerList = ({
  customers,
  selectedUserAppointment,
  handleUserAppointmentSelect,
  openCustomerCard,
  appointmentId,
  fetchAppointmentData,
  fixedAppointmentId,
  fixedAppointmentMemberCustomerIds,
  serviceId,
}: {
  appointmentId: number;
  customers: Customer[];
  handleUserAppointmentSelect: (userAppointmentId: number) => void;
  selectedUserAppointment: number;
  openCustomerCard: (customerId: number) => void;
  fetchAppointmentData: (appointmentId: number) => void;
  fixedAppointmentId: number | null;
  fixedAppointmentMemberCustomerIds: number[];
  serviceId?: number;
}) => {
  const canDeleteAppointments = useUserPermissionsStore(
    (state: { permissions: { restrict_appointment_delete?: boolean } }) =>
      !state.permissions.restrict_appointment_delete,
  );

  const fixedMemberSet = useMemo(
    () => new Set(fixedAppointmentMemberCustomerIds),
    [fixedAppointmentMemberCustomerIds],
  );

  const [
    isAddFixedMemberModalOpen,
    { open: openAddFixedMemberModal, close: closeAddFixedMemberModal },
  ] = useDisclosure(false);
  const [fixedMemberToAdd, setFixedMemberToAdd] = useState<{
    customerId: number;
    name: string;
    lastName: string;
  } | null>(null);
  const [activeUserAppointmentId, setActiveUserAppointmentId] = useState<
    number | null
  >(null);
  const [activePaymentIntentId, setActivePaymentIntentId] = useState<
    string | null | undefined
  >(null);

  const [
    isRemoveCustomerFromAppointmentModalOpen,
    {
      open: openRemoveCustomerFromAppointmentModal,
      close: closeRemoveCustomerFromAppointmentModal,
    },
  ] = useDisclosure(false);

  const [
    isRemoveFixedMemberModalOpen,
    { open: openRemoveFixedMemberModal, close: closeRemoveFixedMemberModal },
  ] = useDisclosure(false);
  const [fixedMemberToRemove, setFixedMemberToRemove] = useState<{
    customerId: number;
    name: string;
  } | null>(null);

  const handleOpenRemoveModal = (
    userAppointmentId: number,
    paymentIntentId: string | null | undefined,
  ) => {
    setActiveUserAppointmentId(userAppointmentId);
    setActivePaymentIntentId(paymentIntentId);
    openRemoveCustomerFromAppointmentModal();
  };

  const activeCustomers = customers.filter((c) => !c.isDeleted);
  const deletedCustomers = customers.filter((c) => c.isDeleted);

  return (
    <div className="top-0 flex-1 pr-1 md:sticky">
      {activeUserAppointmentId && (
        <RemoveCustomerFromAppointmentModal
          isOpen={isRemoveCustomerFromAppointmentModalOpen}
          customerId={
            customers.find(
              (customer) =>
                customer.userAppointmentId === activeUserAppointmentId,
            )?.customerId
          }
          onClose={() => {
            closeRemoveCustomerFromAppointmentModal();
            fetchAppointmentData(appointmentId);
          }}
          appointmentId={appointmentId}
          userAppointmentId={activeUserAppointmentId}
          paymentIntentId={activePaymentIntentId}
        />
      )}

      {fixedAppointmentId != null && (
        <RemoveFixedMemberModal
          isOpen={isRemoveFixedMemberModalOpen}
          onClose={closeRemoveFixedMemberModal}
          fixedAppointmentId={fixedAppointmentId}
          member={fixedMemberToRemove}
          onRemoved={() => fetchAppointmentData(appointmentId)}
        />
      )}

      {fixedAppointmentId != null && (
        <AddFixedMemberModal
          isOpen={isAddFixedMemberModalOpen}
          onClose={closeAddFixedMemberModal}
          fixedAppointmentId={fixedAppointmentId}
          serviceId={serviceId}
          presetCustomer={fixedMemberToAdd ?? undefined}
          onAdded={() => fetchAppointmentData(appointmentId)}
        />
      )}

      {activeCustomers.length > 0 ? (
        activeCustomers.map((customer, index) => (
          <Fragment key={customer.userAppointmentId}>
            <Button
              variant={
                customer.userAppointmentId === selectedUserAppointment
                  ? "bordered"
                  : "light"
              }
              onPress={() => {
                handleUserAppointmentSelect(customer.userAppointmentId);
              }}
              isDisabled={!customer.show}
              className="flex h-auto w-full items-start justify-between gap-2.5 rounded-lg p-2 py-2"
              key={customer.userAppointmentId}
              data-identifier="customer-list-item"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <p className={cn(customer.show ? "" : "text-foreground-500")}>
                    {customer.name} {customer.lastName}
                  </p>
                  {customer.paymentStatus === "succeeded" ? (
                    <BsCurrencyEuro
                      size=".8rem"
                      aria-label={t`Plačano na spletu`}
                      data-identifier="customer-list-paid-online"
                    />
                  ) : null}
                  {customer.isFirstAppointment ? (
                    <Chip color="secondary" size="sm" variant="flat">
                      <Trans>Nova stranka</Trans>
                    </Chip>
                  ) : null}
                  {fixedMemberSet.has(customer.customerId) ? (
                    <Chip color="secondary" size="sm" variant="flat">
                      <Trans>Redna stranka</Trans>
                    </Chip>
                  ) : null}
                  <Chip
                    color={
                      customer.customerDidAttend === 1 ? "success" : "danger"
                    }
                    size="sm"
                    variant="flat"
                  >
                    {customer.customerDidAttend === 1 ? (
                      <Trans>Prisoten</Trans>
                    ) : (
                      <Trans>Neprihod</Trans>
                    )}
                  </Chip>
                  {customer.customerSubscriptionId ? (
                    <Chip
                      classNames={{
                        base: "max-w-full min-w-0",
                        content:
                          "block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
                      }}
                      variant="bordered"
                      color={
                        (customer.subscriptionStatus ??
                          customer.subscription?.status) === "active"
                          ? "primary"
                          : "warning"
                      }
                      size="sm"
                    >
                      {customer.subscription &&
                      customer.subscription.subscriptionName &&
                      customer.subscription.variationName ? (
                        `${customer.subscription.subscriptionName} - ${customer.subscription.variationName}`
                      ) : (
                        <Trans>Paket</Trans>
                      )}
                    </Chip>
                  ) : null}
                  {customer.customerSubscriptionCredit ? (
                    <Chip color="warning" size="sm" variant="flat">
                      <Trans>Uporabljen dodatni termin</Trans>
                    </Chip>
                  ) : null}
                  {customer.subscription &&
                    customer.subscription.validTo &&
                    (() => {
                      const validTo = dayjs(customer.subscription.validTo);
                      const daysUntilExpiry = validTo.diff(dayjs(), "day");

                      if (daysUntilExpiry < 0) {
                        return (
                          <Chip color="danger" size="sm" variant="flat">
                            <Trans>Potekel</Trans>
                          </Chip>
                        );
                      }

                      return daysUntilExpiry <= 7 ? (
                        <Chip color="warning" size="sm" variant="flat">
                          <Trans>Poteče kmalu</Trans>
                        </Chip>
                      ) : null;
                    })()}
                  {customer.isUsedSubscriptionCurrentlyFrozen ? (
                    <Chip color="primary" size="sm" variant="flat">
                      <Trans>Zamrznjen</Trans>
                    </Chip>
                  ) : null}
                </div>
                {customer.customerSubscriptionId &&
                  (() => {
                    const firstUnpaidInvoice =
                      getOutstandingSubscriptionPeriods(
                        customer.subscription?.invoices ?? [],
                        (invoice) => !invoice.memberOwes,
                      ).sort(
                        (a, b) =>
                          new Date(a.periodStart).getTime() -
                          new Date(b.periodStart).getTime(),
                      )[0];

                    return firstUnpaidInvoice ? (
                      <div className="flex items-center gap-1 text-xs text-red-500">
                        <AlertTriangle size={12} />
                        <span>
                          <Trans>Neplačano obdobje </Trans>
                          {firstUnpaidInvoice!.periodStartFormatted} -
                          {firstUnpaidInvoice!.periodEndFormatted}
                        </span>
                      </div>
                    ) : null;
                  })()}
              </div>
              <div className="flex flex-none justify-end">
                <Dropdown placement="bottom-end" showArrow>
                  <DropdownTrigger data-identifier="customer-list-three-dots">
                    <Button isIconOnly className="flex-none" variant="light">
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownTrigger>

                  <DropdownMenu>
                    <DropdownItem
                      onPress={() => openCustomerCard(customer.customerId)}
                      key="customer-card"
                      data-identifier="customer-list-item-customer-card"
                    >
                      <p className="text-sm">
                        <Trans>Kartica stranke</Trans>
                      </p>
                    </DropdownItem>

                    {fixedAppointmentId != null
                      ? (() => {
                          const isFixedMember = fixedMemberSet.has(
                            customer.customerId,
                          );
                          return (
                            <DropdownItem
                              key="toggle-fixed-member"
                              data-identifier={
                                isFixedMember
                                  ? "unmark-fixed-member"
                                  : "mark-fixed-member"
                              }
                              onPress={() => {
                                if (isFixedMember) {
                                  setFixedMemberToRemove({
                                    customerId: customer.customerId,
                                    name: `${customer.name} ${customer.lastName}`.trim(),
                                  });
                                  openRemoveFixedMemberModal();
                                } else {
                                  setFixedMemberToAdd({
                                    customerId: customer.customerId,
                                    name: customer.name,
                                    lastName: customer.lastName,
                                  });
                                  openAddFixedMemberModal();
                                }
                              }}
                            >
                              <p className="text-sm">
                                {isFixedMember ? (
                                  <Trans>Odstrani iz rednih strank</Trans>
                                ) : (
                                  <Trans>Označi kot redno stranko</Trans>
                                )}
                              </p>
                            </DropdownItem>
                          );
                        })()
                      : null}

                    {canDeleteAppointments ? (
                      <DropdownItem
                        key="remove-customer-from-appointment-item"
                        data-identifier="remove-customer-from-appointment-item"
                        color="danger"
                        variant="flat"
                        onPress={() => {
                          handleOpenRemoveModal(
                            customer.userAppointmentId,
                            customer.paymentIntentId,
                          );
                        }}
                      >
                        <p className="text-sm">
                          <Trans>Odstrani stranko s termina</Trans>
                        </p>
                      </DropdownItem>
                    ) : null}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Button>

            {index < activeCustomers.length - 1 && <Divider className="my-2" />}
          </Fragment>
        ))
      ) : (
        <p className="text-foreground-500 text-center text-sm">
          <Trans>Ni strank na tem terminu</Trans>
        </p>
      )}

      <Divider className="my-2" />

      <WaitlistEntries
        appointmentId={appointmentId}
        openCustomerCard={openCustomerCard}
        onBookSuccess={() => fetchAppointmentData(appointmentId)}
      />

      {deletedCustomers.length > 0 ? (
        <div className="mt-2">
          <LimeAccordion
            variant="bordered"
            itemClasses={{
              trigger: "cursor-pointer",
            }}
          >
            <AccordionItem
              textValue={t`Odstranjene stranke`}
              startContent={<UserX size={16} />}
              title={
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium">
                    <Trans>Odstranjene stranke</Trans>
                  </p>
                  <span className="text-default-500 text-xs">
                    ({deletedCustomers.length})
                  </span>
                </div>
              }
            >
              <div className="flex flex-col gap-2">
                {deletedCustomers.map((customer) => (
                  <Button
                    key={customer.userAppointmentId}
                    variant={
                      customer.userAppointmentId === selectedUserAppointment
                        ? "bordered"
                        : "light"
                    }
                    className="flex h-auto w-full justify-between rounded-lg p-2 py-2"
                    data-identifier="customer-list-item-deleted"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <p className="text-default-400">
                          {customer.name} {customer.lastName}
                        </p>
                        {getDisplayedCancelReason(customer) ? (
                          <p className="text-foreground-500 text-xs">
                            {getDisplayedCancelReason(customer)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex justify-end gap-1">
                      <Dropdown placement="bottom-end" showArrow>
                        <DropdownTrigger data-identifier="customer-list-three-dots-deleted">
                          <Button isIconOnly variant="light">
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownTrigger>

                        <DropdownMenu>
                          <DropdownItem
                            onPress={() =>
                              openCustomerCard(customer.customerId)
                            }
                            key="customer-card-deleted"
                            data-identifier="customer-list-item-customer-card-deleted"
                          >
                            <p className="text-sm">
                              <Trans>Kartica stranke</Trans>
                            </p>
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </Button>
                ))}
              </div>
            </AccordionItem>
          </LimeAccordion>
        </div>
      ) : null}
    </div>
  );
};
